import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Avatar,
  InputAdornment,
  CircularProgress,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { showErrorToast } from "utils/toastNotification";

export default function AddParticipantsModal({
  open,
  onClose,
  meeting,
  employees = [],
  onAddParticipants,
  loading = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");

  const currentParticipantIds = (meeting?.participants || []).map((p) => p.employee_id);

  const availableEmployees = employees.filter(
    (emp) => !currentParticipantIds.includes(emp.id)
  );

  const filteredEmployees = availableEmployees.filter((emp) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    const name = `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();
    const email = (emp.email || "").toLowerCase();
    const dept = (emp.department_name || "").toLowerCase();
    return name.includes(term) || email.includes(term) || dept.includes(term);
  });

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAdd = () => {
    if (selectedIds.length === 0) {
      showErrorToast("Please select at least one person to add");
      return;
    }
    onAddParticipants(meeting.id, selectedIds);
    setSelectedIds([]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "#111c44" : "#ffffff",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
          py: 2.5,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
              color: "#10b981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonAddIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? "#ffffff" : "#1b2559" }}>
              Add People to Meeting
            </Typography>
            <Typography sx={{ color: isDark ? "#a3aed0" : "#707eae", fontSize: "0.8rem" }}>
              {meeting?.title}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: isDark ? "#a3aed0" : "#707eae" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <TextField
          placeholder="Search team members..."
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: isDark ? "#a3aed0" : "#a0aec0" }} />
              </InputAdornment>
            ),
            sx: { borderRadius: "12px", fontSize: "0.85rem", mb: 2 },
          }}
        />

        {availableEmployees.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center", color: "text.secondary" }}>
            All company employees are already added to this meeting!
          </Box>
        ) : filteredEmployees.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            No employees match "{search}"
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: 300,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {filteredEmployees.map((emp) => {
              const isSelected = selectedIds.includes(emp.id);
              const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.email;
              return (
                <Box
                  key={emp.id}
                  onClick={() => handleToggle(emp.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    bgcolor: isSelected
                      ? (isDark ? "rgba(245,158,11,0.12)" : "#fef3c7")
                      : (isDark ? "#0b1437" : "#ffffff"),
                    borderColor: isSelected
                      ? "#f59e0b"
                      : (isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"),
                    "&:hover": {
                      borderColor: "#f59e0b",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={emp.profile_pic_url}
                      alt={name}
                      sx={{ width: 36, height: 36, fontSize: "0.8rem", bgcolor: "#f59e0b" }}
                    >
                      {name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: isDark ? "#ffffff" : "#1b2559" }}>
                        {name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#a3aed0" : "#707eae" }}>
                        {emp.designation_name || emp.department_name || emp.email}
                      </Typography>
                    </Box>
                  </Stack>
                  {isSelected && (
                    <CheckCircleIcon sx={{ fontSize: 20, color: "#f59e0b" }} />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
          py: 2,
          px: 3,
          bgcolor: isDark ? "#0b1437" : "#f8fafc",
        }}
      >
        <Button onClick={onClose} color="inherit" sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={loading || selectedIds.length === 0}
          onClick={handleAdd}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "#ffffff",
            borderRadius: "10px",
            px: 3,
            py: 0.9,
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
            },
          }}
        >
          Add {selectedIds.length > 0 ? `(${selectedIds.length}) People` : "People"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
